import dateFormatter from 'date-and-time';
import ru from 'date-and-time/locale/ru';

import {isTranslate} from './lang';

const defaultTimeZone = 'Europe/Moscow';

export interface IDateOptions {
    tzString?: string | null,
    format?: string | null,
    nullValue?: string | null,
}

// Параметры по умолчанию
const defaultDateOptions: IDateOptions = {
    tzString: null,
    format: !isTranslate() ? 'HH:mm, DD.MM.YYYY' : 'HH:mm, YYYY-MM-DD',
    nullValue: '-',
};

/**
 * Форматирование даты
 * @param dateString - Дата
 * @param options - Опции
 * @return string
 */
export function getFormattedDate(
    dateString?: Date | string | null,
    options: IDateOptions = defaultDateOptions,
): string {
    options = {...defaultDateOptions, ...options};
    dateFormatter.locale(ru);
    // Если не передан dateString
    if (null === dateString || typeof dateString === 'undefined') {
        return <string>options.nullValue ?? defaultDateOptions.nullValue;
    }

    const convertedDate = convertTZ(dateString, options.tzString);
    return dateFormatter.format(convertedDate, <string>options.format ?? defaultDateOptions.format);
}

export function currentDatePlusHours(hours: number): Date {
    const date = new Date();
    date.setTime(date.getTime() + hours * 60 * 60 * 1000);

    return date;
}

export function convertTZ(date: string | Date, tzString: string | null | undefined) {
    return new Date(getDate(date)
        .toLocaleString(
            'en-US',
            {timeZone: tzString ?? getClientDefaultTimeZone()},
        ));
}

/**
 * Обратная операция к convertTZ: берёт дату, чьи локальные Y/M/D/H/M нужно
 * трактовать как wall-clock время в tzString, и возвращает реальный момент времени
 */
export function zonedTimeToUtc(date: Date, tzString?: string | null): Date {
    const tz = tzString ?? getClientDefaultTimeZone();
    const asUTC = Date.UTC(
        date.getFullYear(), date.getMonth(), date.getDate(),
        date.getHours(), date.getMinutes(), date.getSeconds(),
    );
    const guess = new Date(asUTC);
    // Обе строки парсятся как локальное время окружения, поэтому его смещение сокращается в разности
    const utcRead = new Date(guess.toLocaleString('en-US', {timeZone: 'UTC'}));
    const tzRead = new Date(guess.toLocaleString('en-US', {timeZone: tz}));
    const tzOffset = tzRead.getTime() - utcRead.getTime();
    return new Date(asUTC - tzOffset);
}

function getDate(date: string | Date): Date {
    return <Date>(typeof date === 'string' ? new Date(date) : date);
}

export function getClientDefaultTimeZone() {
    if (typeof Intl === 'undefined') {
        return defaultTimeZone;
    }

    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function getMinutesFromDayBeginning(
    dateString: Date | string,
    tzString?: string | null,
): number {
    const date = new Date(convertTZ(dateString, tzString));
    return date.getHours() * 60 + date.getMinutes();
}

export function secondsToFormattedString(seconds: number): string {
    seconds = Number(seconds);
    const d = Math.floor(seconds / (3600*24));
    const h = Math.floor(seconds % (3600*24) / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = Math.floor(seconds % 60);
    const display = [];

    d > 0 && display.push(`${d}\u00A0дн.`);
    h > 0 && display.push(`${h}\u00A0час`);
    m > 0 && display.push(`${m}\u00A0мин`);
    s > 0 && d === 0 && h === 0 && display.push(`${s}\u00A0сек`);

    if (display.length === 0) {
        display.push('0\u00A0сек');
    }

    return display.join(', ');
}

export function secondsToRoundedMinutes(seconds: number): number {
    return Math.round(seconds / 60);
}
/**
 * Разница в днях между двумя датами. Если первая дата раньше второй, то значение будет отрицательным
 * @param date1 - первая дата
 * @param date2 - вторая дата
 * @return number
 */
export function getDiffInDays(
    date1: Date | string,
    date2: Date | string,
):number {
    const diffTime = getDate(date1).getTime() - getDate(date2).getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
